import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import type { RegistryDocument } from '../api/types';
import { useSetAllPositions } from '../api/planning-hooks';
import { ProjectEditModal } from '../components/ProjectEditModal';
import { ProjectTable } from '../components/ProjectTable';
import { FilterBar } from '../components/FilterBar';
import { RegistryGate } from '../components/RegistryGate';
import { SummaryChips } from '../components/SummaryChips';
import { CreateButton, FilteredEmptyState, PageHeader } from '../components/view-parts';
import { SORT_LABELS, matchesFilters, sortProjects } from '../lib/filters';
import type { ProjectTableSortKey, SortDirection } from '../lib/filters';
import { moveToTop, orderedIds, sortProjectsAllManual } from '../lib/manual-order';
import { useManualOrder } from '../hooks/useManualOrder';
import { useQueryParams } from '../lib/query-params';
import { useFilters, useRegistryFilterContext } from '../state/FiltersProvider';

/**
 * One key set for BOTH sort controls (toolbar select + column headers). The
 * four shared keys keep `SORT_LABELS`; the table-only ones are local —
 * putting them into `SORT_LABELS` would leak «поток/категория/часы» into the
 * «По потокам» section select, which spreads the same map.
 */
const TABLE_SORT_LABELS: Record<ProjectTableSortKey, string> = {
  ...SORT_LABELS,
  stream: 'поток',
  category: 'категория',
  hours: 'часы',
  manual: 'Вручную',
};

const TABLE_SORT_KEYS = Object.keys(TABLE_SORT_LABELS) as ProjectTableSortKey[];
const DEFAULT_SORT: ProjectTableSortKey = 'priority';

/** F-19: «Все проекты» — one table, every filter, sortable, with a count note. */
export function ProjectsPage(): ReactElement {
  return <RegistryGate label="Загрузка реестра">{(registry) => <ProjectsTable registry={registry} />}</RegistryGate>;
}

function ProjectsTable({ registry }: { registry: RegistryDocument }): ReactElement {
  const { filters, setFilters, isActive } = useFilters();
  const { maps } = useRegistryFilterContext();
  const { params, setParams } = useQueryParams();
  const setAllPositions = useSetAllPositions();
  // The modal edits by ID, not by captured object: after a 409 the hook heals
  // the cache with the fresh document, `registry.projects` changes, and the
  // dialog re-opens on the state that is actually stored (not on the stale row).
  const [editingId, setEditingId] = useState<string | null | undefined>(undefined);
  const /** undefined = closed (or the id vanished), null = creating, Project = editing */
    editing =
      editingId === undefined
        ? undefined
        : editingId === null
          ? null
          : registry.projects.find((project) => project.id === editingId);

  const sort = TABLE_SORT_KEYS.includes(params.sort as ProjectTableSortKey) ? (params.sort as ProjectTableSortKey) : DEFAULT_SORT;
  // `dir` is absent in the canonical URL = asc for every key.
  const dir: SortDirection = params.dir === 'desc' ? 'desc' : 'asc';
  // The manual mode (F-28) is the only one with its own order operations.
  const manual = sort === 'manual';

  /**
   * Sort state transitions (documented choice, kept simple). Header click
   * cycle: asc → desc → back to the default («приоритет» asc). Only the
   * (priority, asc) state drops BOTH keys — that is the default view, so its
   * URL is canonical (STYLEGUIDE §6); `dir` is written only as `desc`, an
   * absent `dir` already reads as asc. «Вручную» (F-28) is the exception: it
   * has NO desc — a reversed manual order would make the row's «наверх»
   * button a lie — so the active column cycles straight back to the default.
   * Filter changes never touch these keys (FilterBar writes only its own
   * params + page).
   */
  const applySort = (key: ProjectTableSortKey, direction: SortDirection) => {
    if (key === DEFAULT_SORT && direction === 'asc') setParams({ sort: null, dir: null });
    else setParams({ sort: key, dir: direction === 'asc' ? null : 'desc' });
  };
  /** Header click: inactive column → asc; active asc → desc («Вручную»: none);
   *  active desc (or the manual column) → default. */
  const onSortColumn = (key: ProjectTableSortKey) => {
    if (sort !== key) applySort(key, 'asc');
    else if (key === 'manual' || dir === 'asc') applySort(DEFAULT_SORT, 'asc');
    else applySort(key, 'desc');
  };
  /** The select picks the column only (never flips direction); re-picking the
   *  active key is a no-op and keeps the URL untouched. */
  const onSortSelect = (key: ProjectTableSortKey) => {
    if (key !== sort) applySort(key, 'asc');
  };

  /**
   * F-28: the FULL global order, UNFILTERED — a drop or «наверх» always sends
   * every project of the registry, so the manual order stays a property of
   * the whole list (mirrors StreamSection, which sends the whole stream even
   * when a filter hides part of it).
   */
  const allSorted = useMemo(() => sortProjectsAllManual(registry.projects), [registry.projects]);
  const allOrder = useMemo(() => orderedIds(allSorted), [allSorted]);

  /** One reorder = optimistic cache update + POST with the FULL new order. */
  const applyOrder = (nextIds: readonly string[] | null) => {
    if (!nextIds || nextIds.length === 0) return;
    setAllPositions.mutate({ version: registry.version, orderedIds: [...nextIds] });
  };

  const controls = useManualOrder({ enabled: manual, busy: setAllPositions.isPending, order: allOrder, onApply: applyOrder });

  /** Global position (index + 1) of EVERY project over the full list — what
   *  the «№» column shows even when filters hide rows (gaps included). */
  const positionOf = useMemo(() => {
    const map = new Map<string, number>();
    for (const [index, project] of allSorted.entries()) {
      map.set(project.id, index + 1);
    }
    return map;
  }, [allSorted]);

  /** «наверх» (F-28): the project goes to GLOBAL position 1, filters or not. */
  const sendToTop = (id: string) => applyOrder(moveToTop(allOrder, id));

  const visible = useMemo(() => {
    if (!maps) return [];
    const filtered = registry.projects.filter((project) => matchesFilters(project, filters, maps));
    // «Вручную» ranks by the global `allPosition` (no direction); the other
    // keys go through the shared table sort with their tie-breaks.
    if (manual) return sortProjectsAllManual(filtered);
    return sortProjects(filtered, sort, undefined, dir, {
      streamsById: maps.streamsById,
      categoriesById: maps.categoriesById,
    });
  }, [registry.projects, filters, manual, sort, dir, maps]);

  return (
    <>
      <PageHeader
        title="Все проекты"
        sub={
          <span>
            Показано <strong className="num-inline">{visible.length}</strong> из {registry.projects.length} проектов
            реестра
          </span>
        }
        actions={<CreateButton label="Новый проект" onClick={() => setEditingId(null)} />}
      />

      <SummaryChips />

      <FilterBar
        extra={
          <>
            <div className="toolbar__field">
              <label className="toolbar__label" htmlFor="filter-category">
                Категория
              </label>
              <select
                id="filter-category"
                className="input input--compact"
                value={filters.categoryId ?? ''}
                onChange={(event) => setFilters({ ...filters, categoryId: event.target.value || undefined })}
              >
                <option value="">Любая</option>
                {registry.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="toolbar__field">
              <label className="toolbar__label" htmlFor="projects-sort">
                Сортировка
              </label>
              <select
                id="projects-sort"
                className="input input--compact"
                value={sort}
                onChange={(event) => onSortSelect(event.target.value as ProjectTableSortKey)}
              >
                {TABLE_SORT_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {TABLE_SORT_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
          </>
        }
      />

      {/* The team select lives in the shared FilterBar (F-19 on every view), so
          «Найдено: 0 из N» already carries the news when filters cut everything:
          an empty table block would only repeat it (owner feedback #2). */}
      {visible.length === 0 && isActive ? null : (
        <section className="card card--flush">
          <ProjectTable
            projects={visible}
            registry={registry}
            showStream
            onEdit={(project) => setEditingId(project.id)}
            emptyState={<FilteredEmptyState />}
            caption="Все проекты реестра"
            sortable={{ sortKey: sort, direction: dir, onSort: onSortColumn }}
            // The whole order apparatus (drag column, «№», «наверх») exists
            // ONLY in «Вручную». A permanently visible DISABLED handle outside
            // the mode would read as broken (owner feedback: hovering it shows
            // the blocked cursor) — on «По потокам» the precedent works only
            // because «Вручную» is that view's DEFAULT; here it is opt-in.
            manualOrder={manual ? controls : undefined}
            positionOf={manual ? positionOf : undefined}
            onSendToTop={manual ? sendToTop : undefined}
          />
        </section>
      )}

      {editing !== undefined ? (
        <ProjectEditModal open onClose={() => setEditingId(undefined)} registry={registry} project={editing} />
      ) : null}
    </>
  );
}
